import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import DataTable from "react-data-table-component";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";

function AcademicCalendar() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [sections, setSections] = useState([]);
  const [classSection, setClassSection] = useState([]);
  const [selectedClassSection, setSelectedClassSection] = useState([]);
  const [selectedCenterId, setSelectedCenterId] = useState(
    savedCenterId || null
  );
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [holidayTypes, setHolidayTypes] = useState([]);
  const [calendars, setCalendars] = useState([]);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      colorCode: "",
      centerId: "",
      centerClassId: "",
      sectionId: "",
      remarks: "",
      stdAttnCalenderId: "",
      fromDate: "",
      toDate: "",
      year: selectedYear,
      organizationId: organizationId,
      createdBy: localStorage.getItem("userId"),
    },
  });

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );
          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
            fetchCalendars(selectedCenter.id, selectedYear);
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
  }, [setValue, triggerRerender, savedCenterId, selectedYear]);

  const fetchClassesByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassesData(response);
      } else {
        setClassesData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchClassSecionByCenterId = async (centerId) => {
    try {
      if (centerId) {
        const response = await get(
          `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassSection(response);
      } else {
        setClassSection([]);
      }
    } catch (error) {
      console.error("Error fetching class sections:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId) {
      fetchClassesByCenterId(selectedCenterId);
      fetchClassSecionByCenterId(selectedCenterId);
    }
  }, [selectedCenterId]);

  const getAllClassSectionsByClassIdAndCenterId = async (classId, centerId) => {
    try {
      if (classId && centerId) {
        const response = await get(
          `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${classId}&centerId=${centerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSections(response);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  useEffect(() => {
    if (selectedCenterId && selectedClassId) {
      getAllClassSectionsByClassIdAndCenterId(
        selectedCenterId,
        selectedClassId
      );
    }
  }, [selectedCenterId, selectedClassId, triggerRerender]);

  useEffect(() => {
    const fetchHolidayTypes = async () => {
      try {
        const response = await get("CenterAttendence/GetHolidayType", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHolidayTypes(response.holidayTypes);
      } catch (error) {
        console.error("Error fetching holiday types:", error);
      }
    };

    fetchHolidayTypes();
  }, [setValue, triggerRerender]);

  const fetchCalendars = async (centerId, year, classSectionIds = []) => {
    try {
      const classSectionParams =
        classSectionIds.length > 0
          ? classSectionIds.map((id) => `${id}`).join(",")
          : "0";
      const url = `CenterAttendence/GetCalenderHolidays?Year=${year}&CenterId=${centerId}&classSectionIds=${classSectionParams}`;
      const response = await get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCalendars(response.holidays);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    if (selectedCenterId) {
      fetchCalendars(selectedCenterId, year);
    }
  };

  const handleEdit = async (row) => {
    try {
      const response = await get(
        `CenterAttendence/GetCalenderHoliday?year=${selectedYear}&centerId=${row.centerId}&id=${row.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const holiday = response.holiday;

      const selectedCenter = centersData.find(
        (center) => center.id === holiday.centerId
      );

      // Assuming holiday.classSectionIds is an array of IDs from the API
      const selectedSections = classSection.filter((section) =>
        holiday.classSectionIds.includes(section.id)
      );
      const selectedSectionOptions = selectedSections.map((section) => ({
        value: section.id,
        label: section.name,
      }));

      // Update the selectedClassSection state for the UI
      setSelectedClassSection(
        selectedSectionOptions.map((option) => option.value)
      );

      reset({
        id: holiday.id,
        name: holiday.name,
        remarks: holiday.remarks,
        colorCode: holiday.colorCode,
        centerId: {
          value: selectedCenter?.id || "",
          label: selectedCenter?.name || "",
        },
        StdAttnCalenderDayTypeId: holiday.stdAttnCalenderDayTypeId,
        classSectionId: selectedSectionOptions, // Multi-select expects array of { value, label }
        organizationId: holiday.organizationId || organizationId,
        fromDate: holiday.fromDate ? holiday.fromDate.split("T")[0] : "",
        toDate: holiday.toDate ? holiday.toDate.split("T")[0] : "",
        year: selectedYear,
        createdBy: holiday.createdBy || localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing holiday:", error);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this record?"
    );
    if (!confirmed) return;

    try {
      const response = await get(
        `CenterAttendence/DeleteCalenderHoliday?year=${selectedYear}&centerId=${row.centerId}&id=${row.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.success) {
        toast.success(response.message || "Success!");
        fetchCalendars(selectedCenterId, selectedYear);
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          colorCode: "",
          centerId: selectedCenterId,
          centerClassId: "",
          sectionId: "",
          remarks: "",
          stdAttnCalenderId: "",
          fromDate: "",
          toDate: "",
          year: selectedYear,
          organizationId: organizationId,
          createdBy: localStorage.getItem("userId"),
        });
      } else {
        console.error("Error deleting holiday:", response.message);
      }
    } catch (error) {
      console.error("Error on delete:", error);
    }
  };

  const columns = [
    { name: "Name", selector: (row) => row.name, sortable: true },
    {
      name: "Date (From — To)",
      selector: (row) =>
        `${new Date(row.fromDate).toLocaleDateString("en-GB")} — ${new Date(
          row.toDate
        ).toLocaleDateString("en-GB")}`,
      sortable: true,
      width: "200px",
    },
    { name: "Type", selector: (row) => row.calTypeDesc, sortable: true },
    {
      name: "Days Count",
      selector: (row) => row.consecutiveDaysCount,
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div>
          <button
            type="button"
            className="btn p-0"
            onClick={() => handleEdit(row)}
          >
            <i className="fas fa-pencil"></i>
          </button>
          <button
            type="button"
            className="btn p-0 ms-2"
            onClick={() => handleDelete(row)}
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      ),
    },
  ];

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      centerId: data.centerId.value,
      centerClassId: data.centerClassId?.value || 0,
      classSectionIds: selectedClassSection,
      sectionId: 0,
    };

    try {
      const response = await post(
        "CenterAttendence/AddUpdateAcademicHolidays",
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.success) {
        toast.success(response.message || "Success!");
        fetchCalendars(formData.centerId, selectedYear);
        reset({
          id: 0,
          name: "",
          colorCode: "",
          centerId: data.centerId,
          centerClassId: "",
          sectionId: "",
          remarks: "",
          stdAttnCalenderId: "",
          fromDate: "",
          toDate: "",
          year: selectedYear,
          organizationId: organizationId,
          createdBy: localStorage.getItem("userId"),
        });
        handleClassSectionChange([]);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleClassSectionChange = (selectedOptions) => {
    const selectedValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setSelectedClassSection(selectedValues);
    if (selectedCenterId) {
      fetchCalendars(selectedCenterId, selectedYear, selectedValues);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Academic Calendar</h2>
      </div>

      <div className="row g-3">
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Center<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centersData.map((center) => ({
                          value: center.id,
                          label: center.name,
                        }))}
                        isDisabled={savedCenterId !== "0"}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("centerId", null);
                            setSelectedCenterId(null);
                            fetchCalendars(0, selectedYear);
                          } else {
                            setValue("centerId", selectedOption);
                            setSelectedCenterId(selectedOption.value);
                            fetchCalendars(selectedOption.value, selectedYear);
                          }
                          trigger("centerId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable={!savedCenterId}
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Year Group Section
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="classSectionId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={classSection.map((classItem) => ({
                          value: classItem.id,
                          label: classItem.name,
                        }))}
                        onChange={(selectedOptions) => {
                          handleClassSectionChange(selectedOptions);
                          field.onChange(selectedOptions);
                        }}
                        value={selectedClassSection.map((id) => ({
                          value: id,
                          label: classSection.find((item) => item.id === id)
                            ?.name,
                        }))}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isMulti
                        isClearable
                      />
                    )}
                  />
                  {errors.classSectionId && (
                    <small className="text-danger">
                      {errors.classSectionId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-new">
                    Event / Holiday Type
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="StdAttnCalenderDayTypeId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="form-select"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <option value="">Select</option>
                        {holidayTypes.map((holidayType) => (
                          <option key={holidayType.id} value={holidayType.id}>
                            {holidayType.name}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  {errors.StdAttnCalenderDayTypeId && (
                    <small className="text-danger">
                      {errors.StdAttnCalenderDayTypeId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("name", {
                      required: "This field is required.",
                      maxLength: {
                        value: 75,
                        message: "Maximum of 75 characters allowed.",
                      },
                    })}
                    type="text"
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("name")}
                  />
                  {errors.name && (
                    <small className="text-danger">{errors.name.message}</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Color Code<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    style={{
                      width: "100%",
                      border: "none",
                      borderRadius: "5px",
                    }}
                    {...register("colorCode", {
                      required: "This field is required.",
                    })}
                    type="color"
                    className=""
                    placeholder=""
                  />
                  {errors.colorCode && (
                    <small className="text-danger">
                      {errors.colorCode.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    From Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("fromDate", {
                      required: "This field is required.",
                    })}
                    type="date"
                    className="form-control"
                    placeholder=""
                    min={`${new Date().getFullYear()}-01-01`}
                    max={`${new Date().getFullYear()}-12-31`}
                  />
                  {errors.fromDate && (
                    <small className="text-danger">
                      {errors.fromDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    To Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("toDate", {
                      required: "This field is required.",
                      validate: (value) => {
                        const fromDate = new Date(watch("fromDate"));
                        const toDate = new Date(value);
                        return (
                          toDate >= fromDate || "Cannot be less than From Date."
                        );
                      },
                    })}
                    type="date"
                    className="form-control"
                    placeholder=""
                    min={`${new Date().getFullYear()}-01-01`}
                    max={`${new Date().getFullYear()}-12-31`}
                  />
                  {errors.toDate && (
                    <small className="text-danger">
                      {errors.toDate.message}
                    </small>
                  )}
                </div>
                <div className="col-12">
                  <label className="form-label form-label-new">Remarks</label>
                  <textarea
                    {...register("remarks", {
                      maxLength: {
                        value: 250,
                        message: "Maximum of 250 characters allowed.",
                      },
                    })}
                    className="form-control"
                    placeholder=""
                    onChange={handleChangeOfInputWithLengthCheck("remarks")}
                  ></textarea>
                  {errors.remarks && (
                    <small className="text-danger">
                      {errors.remarks.message}
                    </small>
                  )}
                </div>
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <DataTable
                columns={columns}
                data={calendars}
                pagination
                dense
                customStyles={customTableStyles}
                highlightOnHover
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AcademicCalendar;
